import type { Citation, ClaimRead } from './citationTypes'
import { anchorMatches } from './citationTypes'
import type { PRDSections } from './models'

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
function object(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
function id(value: unknown): boolean {
  return typeof value === 'string' && uuid.test(value)
}
function positive(value: unknown): boolean {
  return Number.isInteger(value) && Number(value) > 0
}
function text(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    Array.from(value).length > 0 &&
    Array.from(value).length <= 20_000
  )
}
function span(value: unknown): value is Record<string, unknown> {
  return (
    object(value) &&
    Number.isInteger(value.start_char) &&
    Number(value.start_char) >= 0 &&
    positive(value.end_char) &&
    Number(value.end_char) > Number(value.start_char) &&
    (value.page_number == null || positive(value.page_number))
  )
}
function source(value: unknown): value is Record<string, unknown> {
  return span(value) && id(value.source_id) && id(value.source_version_id)
}
function excerpt(value: Record<string, unknown>): boolean {
  return (
    span(value) &&
    text(value.excerpt) &&
    Array.from(value.excerpt).length === Number(value.end_char) - Number(value.start_char)
  )
}
function attribution(value: unknown): boolean {
  if (value === null) return true
  return (
    object(value) &&
    ['source_text', 'upload_metadata'].includes(String(value.origin)) &&
    ['speaker', 'role', 'organization', 'occurred_on'].every(
      (key) => value[key] == null || typeof value[key] === 'string',
    ) &&
    ['speaker', 'role', 'organization', 'occurred_on'].some((key) => Boolean(value[key]))
  )
}
function metadata(value: unknown): boolean {
  return (
    object(value) &&
    Object.keys(value).length <= 16 &&
    JSON.stringify(value).length <= 4000 &&
    Object.entries(value).every(
      ([key, item]) =>
        /^[a-z][a-z0-9_]{0,63}$/.test(key) &&
        (item === null ||
          typeof item === 'boolean' ||
          (typeof item === 'number' && Number.isFinite(item)) ||
          (typeof item === 'string' && Array.from(item).length <= 500)),
    )
  )
}

export function isCitation(value: unknown): value is Citation {
  if (
    !object(value) ||
    value.schema_version !== 1 ||
    !id(value.id) ||
    !object(value.anchor) ||
    !text(value.anchor.text) ||
    !span(value.anchor) ||
    Array.from(value.anchor.text).length !==
      Number(value.anchor.end_char) - Number(value.anchor.start_char) ||
    !['linked', 'unavailable'].includes(String(value.public_state))
  )
    return false
  const ref = value.reference
  const snapshot = value.snapshot
  if (
    !object(ref) ||
    !object(snapshot) ||
    ref.kind !== snapshot.kind ||
    !metadata(snapshot.metadata ?? {})
  )
    return false
  if (ref.kind === 'source')
    return (
      value.review_state === 'review_required' &&
      source(ref.source) &&
      text(snapshot.excerpt) &&
      Array.from(snapshot.excerpt).length ===
        Number(ref.source.end_char) - Number(ref.source.start_char) &&
      attribution(snapshot.attribution)
    )
  if (value.review_state !== 'reviewed') return false
  if (ref.kind === 'evidence')
    return (
      id(ref.evidence_id) &&
      positive(ref.evidence_revision) &&
      source(ref.source) &&
      text(snapshot.observation) &&
      text(snapshot.rationale) &&
      excerpt(snapshot) &&
      attribution(snapshot.attribution) &&
      snapshot.source_version_id === ref.source.source_version_id &&
      snapshot.start_char === ref.source.start_char &&
      snapshot.end_char === ref.source.end_char &&
      (snapshot.page_number ?? null) === (ref.source.page_number ?? null)
    )
  return (
    ref.kind === 'signal' &&
    id(ref.signal_id) &&
    positive(ref.signal_revision) &&
    text(snapshot.title) &&
    text(snapshot.explanation) &&
    Array.isArray(snapshot.edges) &&
    snapshot.edges.length > 0 &&
    snapshot.edges.length <= 50 &&
    snapshot.edges.every(
      (edge: unknown) =>
        source(edge) &&
        id(edge.evidence_id) &&
        positive(edge.evidence_revision) &&
        ['supports', 'challenges'].includes(String(edge.stance)) &&
        text(edge.rationale) &&
        text(edge.observation) &&
        excerpt(edge),
    )
  )
}

export function conversationCitations(
  value: unknown,
  content: string,
  invalid = false,
): readonly Citation[] {
  const items = parseConversationCitations(value, invalid)
  if (!items.every((item) => anchorMatches(content, item.anchor))) {
    throw new Error('Citation metadata could not be verified. Read the saved message history.')
  }
  return items
}

export function parseConversationCitations(
  value: unknown,
  invalid = false,
): readonly Citation[] {
  const items = value === undefined ? [] : value
  if (
    !Array.isArray(items) ||
    items.length > 50 ||
    (invalid && items.length > 0) ||
    !items.every(
      (item: unknown) =>
        isCitation(item) &&
        typeof item.retrieval_handle === 'string' &&
        /^[A-Za-z0-9_-]{8,64}$/.test(item.retrieval_handle),
    )
  ) {
    throw new Error('Citation metadata could not be verified. Read the saved message history.')
  }
  return items as readonly Citation[]
}

export function validateClaims(
  claims: readonly ClaimRead[] | undefined,
  sections: PRDSections,
): void {
  for (const claim of claims ?? []) {
    if (
      !id(claim.claim_id) ||
      !(claim.section in sections) ||
      !['anchored', 'needs_reanchor'].includes(claim.anchor_state) ||
      (claim.anchor_state === 'anchored' &&
        !anchorMatches(sections[claim.section], claim.anchor)) ||
      !Array.isArray(claim.citations) ||
      claim.citations.length < 1 ||
      claim.citations.length > 20 ||
      !claim.citations.every(
        (item) =>
          isCitation(item) &&
          item.retrieval_handle === null &&
          item.anchor.text === claim.anchor.text &&
          item.anchor.start_char === claim.anchor.start_char &&
          item.anchor.end_char === claim.anchor.end_char,
      )
    ) {
      throw new Error('Claim citation metadata could not be verified. Read the current draft.')
    }
  }
}
